import React from 'react';
import { Icon } from '@/components/primitives/icon/Icon';
import { AutoTooltip } from '@/components/primitives/AutoTooltip';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useAppDispatch } from '@/redux/hooks';
import { setDialog } from '@/redux/reducers/dialog';
import { CreateSiteDialog } from './CreateSiteButton';
import { CreateBuildingDialog } from './CreateBuildingButton';
import { CreateFloorDialog } from './CreateFloorButton';
import { datadogRum } from '@datadog/browser-rum';
import { Dropdown } from 'antd';

interface CreateDropdownButtonProps {
  variant?: 'default' | 'compact';
}

export function CreateDropdownButton({ variant = 'default' }: CreateDropdownButtonProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isCompact = variant === 'compact';

  const handleCreateSite = () => {
    datadogRum.addAction('Open create site dialog', { source: 'create-dropdown' });
    dispatch(
      setDialog({
        content: <CreateSiteDialog />,
        options: {
          className: 'w-[520px] rounded',
        },
      }),
    );
  };

  const handleCreateBuilding = () => {
    datadogRum.addAction('Open create building dialog', { source: 'create-dropdown' });
    dispatch(
      setDialog({
        content: <CreateBuildingDialog />,
        options: {
          className: 'w-[520px] rounded',
        },
      }),
    );
  };

  const handleCreateFloor = () => {
    datadogRum.addAction('Open create floor dialog', { source: 'create-dropdown' });
    dispatch(
      setDialog({
        content: <CreateFloorDialog />,
        options: {
          className: 'w-[520px] rounded',
        },
      }),
    );
  };

  const menuItems = [
    {
      key: 'site',
      label: (
        <div className="flex items-center">
          <Icon name="space" className="mr-2 w-4 h-4" />
          Create Site
        </div>
      ),
      onClick: handleCreateSite,
    },
    {
      key: 'building',
      label: (
        <div className="flex items-center">
          <Icon name="building" className="mr-2 w-4 h-4" />
          Create Building
        </div>
      ),
      onClick: handleCreateBuilding,
    },
    {
      key: 'floor',
      label: (
        <div className="flex items-center">
          <Icon name="layer" className="mr-2 w-4 h-4 text-content-primary" />
          Create Floor
        </div>
      ),
      onClick: handleCreateFloor,
    },
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      placement={isCompact ? 'topLeft' : 'bottomRight'}
      trigger={['click']}
    >
      <div>
        <AutoTooltip label={isCompact ? 'Create' : ''}>
          <button
            className={clsx(
              'whitespace-nowrap rounded flex items-center text-sm justify-center text-content-primary hover:bg-surface-hover border border-default',
              variant === 'default' ? 'py-2 px-4' : '',
              isCompact ? 'py-2 px-2 mb-4' : '',
            )}
            data-id="create-dropdown-button"
          >
            <Icon
              name="plus"
              className={clsx('w-4', { 'mr-2': !isCompact })}
              color="currentColor"
            ></Icon>
            {!isCompact && t('create')}
          </button>
        </AutoTooltip>
      </div>
    </Dropdown>
  );
}
